import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Eye, Printer } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const now = new Date();
export default function SalarySlip() {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [empFilter, setEmpFilter] = useState("all");
  const [emps, setEmps] = useState([]);
  const [slips, setSlips] = useState([]);
  const load = async () => {
    try {
      const [e, p] = await Promise.all([api.get("/employees"), api.get(`/payroll?month=${month}&year=${year}`)]);
      setEmps(e.data); setSlips(p.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [month, year]); // eslint-disable-line
  const empName = (id) => emps.find(e => e.id === id)?.name || "-";
  const empCode = (id) => emps.find(e => e.id === id)?.employee_code || "-";
  const open = (eid, mode) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/salary-slip/${month}/${year}/${eid}/pdf?_=${Date.now()}`;
    if (mode === "print") window.open(url, "_blank")?.print?.();
    else window.open(url, "_blank");
  };
  const filtered = slips.filter(s => empFilter === "all" || s.employee_id === empFilter);

  const pg = usePagination(filtered);
  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div><div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><FileText className="w-3 h-3"/> HRMS</div><h1 className="font-display font-black text-4xl uppercase tracking-tighter">Salary Slip</h1></div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Employee</Label>
            <Select value={empFilter} onValueChange={setEmpFilter}><SelectTrigger data-testid="slip-emp" className="mt-1 rounded-sm bg-secondary border-border w-52"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">All Employees</SelectItem>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}><SelectTrigger data-testid="slip-month" className="mt-1 rounded-sm bg-secondary border-border w-32"><SelectValue/></SelectTrigger>
              <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map(m => <SelectItem key={m} value={String(m)}>{new Date(2000, m - 1).toLocaleString("en", { month: "long" })}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger data-testid="slip-year" className="mt-1 rounded-sm bg-secondary border-border w-24"><SelectValue/></SelectTrigger>
              <SelectContent>{[year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="slips-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr><th className="text-left p-3">Emp Code</th><th className="text-left p-3">Name</th><th className="text-right p-3">Gross</th><th className="text-right p-3">Deductions</th><th className="text-right p-3">Net Pay</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No payroll processed for this period</td></tr>}
            {pg.pageItems.map(s => { const ded = (s.pf || 0) + (s.esi || 0) + (s.tds || 0) + (s.other_deductions || 0); return (
              <tr key={s.id} data-testid={`slip-row-${s.employee_id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{empCode(s.employee_id)}</td><td className="p-3">{empName(s.employee_id)}</td>
                <td className="p-3 text-right font-mono">{inr(s.gross || 0)}</td><td className="p-3 text-right font-mono text-muted-foreground">{inr(ded)}</td>
                <td className="p-3 text-right font-mono font-bold text-primary">{inr(s.net || 0)}</td>
                <td className="p-3 text-right space-x-1">
                  <Button size="sm" variant="ghost" data-testid={`slip-view-${s.employee_id}`} onClick={() => open(s.employee_id, "view")} className="h-7 w-7 p-0"><Eye className="w-3 h-3"/></Button>
                  <Button size="sm" variant="ghost" data-testid={`slip-print-${s.employee_id}`} onClick={() => open(s.employee_id, "print")} className="h-7 w-7 p-0"><Printer className="w-3 h-3"/></Button>
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
